// Правка поста: название, часы в сутки, режим смен, разряд, сколько охранников одновременно.
//
// ## Почему это не косметика
//
// Часы в сутки и число охранников — не подписи на карточке. Из часов по активным постам
// складывается РАСЧЁТНЫЙ НОРМАТИВ объекта за месяц, из норматива считается ставка человека, из
// числа охранников — требуемая укомплектованность. Пост, заведённый с опечаткой «2» вместо
// «12», до 15.09.2026 нельзя было ни поправить, ни снять: на сервере у поста был только путь
// «завести». Единственным выходом оставалось завести второй пост рядом — и тогда норматив
// объекта складывался уже из обоих.
//
// ## Почему часы уходят строкой
//
// Запятая из привычки Excel принимается и заменяется точкой, но само значение доезжает до
// сервера СТРОКОЙ: «63,5» через число с плавающей точкой теряет полчаса на сумме по постам, а
// из этой суммы считается норматив. 63,5 — не выдуманный пример, оно встречается в файлах
// заказчика.
//
// ## Часов больше, чем в сутках, — предупреждение, а не запрет
//
// Если часов в сутки больше, чем 24 на каждого одновременно стоящего, под полем часов сразу,
// по мере ввода, стоит предупреждение (`post-hours.ts`). Сохранить при этом можно: у заказчика
// бывают посты, где часы — это часы нескольких охранников вместе, и решает он.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine, RiShieldUserLine, RiTimeLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { PostPatch } from '@/shared/api/endpoints/org';
import type { DecimalString } from '@/shared/api/types';
import { decimalString } from '@/shared/api/types';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import { usePostsQuery, useScheduleTypesQuery, useSiteQuery } from '@/shared/org/queries';

import { лишниеЧасыПоста } from '../post-hours';
import { разобратьПост } from '../route';
import { usePatchPostMutation } from '../queries';

/** Правка поста — то же право, что и заведение (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

/** Разряды охранника из тз: четвёртый, пятый и шестой. Другого в законе нет. */
const GUARD_CATEGORIES = ['4', '5', '6'] as const;

export function EditPostPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const разбор = разобратьПост(id);
  if (разбор === null) {
    return (
      <EmptyState
        icon={RiShieldUserLine}
        title='Ссылка на правку поста неполная'
        description='В адресе должны быть и объект, и пост. Откройте пост из карточки объекта — оттуда правка открывается с обоими.'
      />
    );
  }
  return <Форма siteId={разбор.siteId} postId={разбор.postId} onClose={onClose} />;
}

function Форма({
  siteId,
  postId,
  onClose,
}: {
  siteId: string;
  postId: string;
  onClose: () => void;
}) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, EDIT_PERMISSION);
  const объект = useSiteQuery(siteId);
  const посты = usePostsQuery(siteId);
  const режимы = useScheduleTypesQuery();
  const правка = usePatchPostMutation(siteId, postId);

  const [черновик, setЧерновик] = useState<{
    название: string;
    часы: string;
    режим: string;
    разряд: string;
    одновременно: string;
    действует: boolean;
  } | null>(null);
  const [ошибки, setОшибки] = useState<{
    название?: string;
    часы?: string;
    одновременно?: string;
  }>({});
  const [заслон, setЗаслон] = useState<string | null>(null);

  if (посты.data === undefined) {
    return (
      <div className='flex flex-col gap-3'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-2/3' />
      </div>
    );
  }

  const пост = посты.data.items.find((строка) => строка.id === postId);
  if (пост === undefined) {
    return (
      <EmptyState
        icon={RiShieldUserLine}
        title='Этого поста нет у выбранного объекта'
        description='Ссылка ведёт на пост другого объекта либо на пост, которого уже нет. Откройте карточку объекта — его посты перечислены на вкладке «Посты».'
      />
    );
  }

  const поля = черновик ?? {
    название: пост.name,
    // Уже записанные часы показываются в том же виде, в каком их примут обратно: не показать
    // их значит стереть при первом же сохранении соседнего поля, и стереть молча.
    часы: пост.hours_per_day,
    режим: пост.schedule_type ?? NO_VALUE,
    разряд: пост.required_guard_category === null ? NO_VALUE : String(пост.required_guard_category),
    одновременно: String(пост.simultaneous_guards),
    действует: пост.is_active,
  };

  function поставить<К extends keyof typeof поля>(ключ: К, значение: (typeof поля)[К]) {
    setЧерновик({ ...поля, [ключ]: значение });
  }

  // Считается по ЧЕРНОВИКУ, а не по записанному: поправили часы или число охранников — и
  // предупреждение ушло тут же, до сохранения.
  const лишние = лишниеЧасыПоста(поля.часы, Number(поля.одновременно));

  function сохранить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗаслон(
        'Править посты может тот, у кого есть право на справочники. Попросите администратора выдать его — набранное никуда не денется.',
      );
      return;
    }
    if (правка.isPending) {
      setЗаслон('Пост уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const найдено: { название?: string; часы?: string; одновременно?: string } = {};
    if (поля.название.trim() === '') {
      найдено.название = 'Название стереть нельзя: по нему пост выбирают при постановке людей.';
    }

    let часы: DecimalString | null = null;
    try {
      часы = decimalString(поля.часы.trim().replace(',', '.'));
    } catch {
      найдено.часы = 'Часы пишутся числом: 12, 18, 24 или 63.5.';
    }

    const одновременно = Number(поля.одновременно);
    if (!Number.isInteger(одновременно) || одновременно < 1) {
      найдено.одновременно = 'Одновременно на посту стоит хотя бы один человек.';
    }

    if (Object.keys(найдено).length > 0 || часы === null) {
      setОшибки(найдено);
      return;
    }

    setОшибки({});
    setЗаслон(null);
    const тело: PostPatch = {
      name: поля.название.trim(),
      hours_per_day: часы,
      // Пустой выбор уходит как «снять», а не как «не трогать»: «разряд не важен» и «режим не
      // задан» — законные ответы, а не незаполненные поля.
      required_guard_category: поля.разряд === NO_VALUE ? null : Number(поля.разряд),
      schedule_type: поля.режим === NO_VALUE ? null : поля.режим,
      simultaneous_guards: одновременно,
      is_active: поля.действует,
    };
    правка.mutate(тело, { onSuccess: onClose });
  }

  return (
    <form onSubmit={сохранить} noValidate className='flex flex-col gap-3'>
      <p className='text-paragraph-xs text-text-sub-600'>
        Объект: {объект.data?.name ?? 'загружаем название…'}
      </p>

      {!можно && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку справочников у вас нет: форму можно заполнить, но сохранить пост не
            выйдет. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <TextField
        id='edit-post-name'
        label='Название поста'
        required
        icon={RiShieldUserLine}
        value={поля.название}
        onChange={(значение) => поставить('название', значение)}
        error={ошибки.название}
      />

      <TextField
        id='edit-post-hours'
        label='Часов в сутки'
        required
        icon={RiTimeLine}
        value={поля.часы}
        onChange={(значение) => поставить('часы', значение)}
        inputMode='decimal'
        error={ошибки.часы}
        hint='Из этого числа складывается расчётный норматив объекта за месяц, а из норматива — ставка человека.'
      />
      {лишние !== null && (
        <Alert.Root variant='lighter' status='warning' size='xsmall' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>{лишние} Сохранению это не мешает: решать вам.</span>
        </Alert.Root>
      )}

      <SelectField
        id='edit-post-schedule'
        label='Режим работы'
        value={поля.режим}
        onChange={(значение) => поставить('режим', значение)}
        options={(режимы.data?.items ?? []).map((строка) => ({
          value: строка.code,
          label: строка.title,
        }))}
        noneLabel='Режим не задан'
        placeholder='Выберите режим'
        pending={режимы.isPending}
        failed={режимы.isError}
        failedError={режимы.error as ApiError | null}
        hint='По режиму строится график смен в табеле. Не задан — смены заполняются вручную либо итогом за месяц.'
      />

      <SelectField
        id='edit-post-category'
        label='Требуемый разряд'
        value={поля.разряд}
        onChange={(значение) => поставить('разряд', значение)}
        options={GUARD_CATEGORIES.map((значение) => ({
          value: значение,
          label: `${значение} разряд`,
        }))}
        noneLabel='Разряд не важен'
        placeholder='Выберите разряд'
        hint='Разряд ограничивает, кого можно поставить на пост.'
      />

      <TextField
        id='edit-post-guards'
        label='Одновременно охранников'
        required
        value={поля.одновременно}
        onChange={(значение) => поставить('одновременно', значение)}
        inputMode='numeric'
        error={ошибки.одновременно}
        hint='Из этого числа складывается требуемая укомплектованность объекта.'
      />

      <CheckboxField
        id='edit-post-active'
        label='Пост действует'
        checked={поля.действует}
        onCheckedChange={(значение) => поставить('действует', значение)}
        hint='Снимите отметку, если пост сняли: он перестанет считаться в нормативе объекта и в укомплектованности, а отмеченные по нему смены останутся в табеле.'
      />

      {правка.isError && <ErrorAlert error={правка.error} />}

      {заслон !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {заслон}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {правка.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
